import React, { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { Panel, Toggle } from 'src/components/app-ui'
import {
  AddMenu,
  ColorField,
  ContrastWarning,
  ListRow,
  RowMenu,
  SortableList,
  move,
} from './parts'
import { FooterPageDialog, LinkDialog, NewPageDialog } from './dialogs'
import { HEADER_BG_SWATCHES, TEXT_SWATCHES, newLinkId } from './navigation-tab'
import BrandFields from './brand-fields'
import useStyles from './styles'
import {
  PortalFooterBar,
  SOCIAL_NETWORKS,
  defaultCopyright,
  networkOf,
} from './portal-bars'

export { SOCIAL_NETWORKS, defaultCopyright }

/** Input saved when it loses focus (copyright, social URLs). */
const BlurInput = ({ value, onSave, className, placeholder }) => {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return (
    <input
      className={className}
      value={draft}
      placeholder={placeholder}
      onChange={event => setDraft(event.target.value)}
      onBlur={() => draft !== value && onSave(draft)}
      onKeyDown={event => event.key === 'Enter' && event.target.blur()}
    />
  )
}

/** Portal › Footer: preview, links, social profiles and appearance. */
const FooterTab = ({
  portal,
  update,
  createPage,
  host,
  shopName,
  workspaceLogo,
  openEditor,
}) => {
  const classes = useStyles()
  const [pageDialog, setPageDialog] = useState(null) // {} new, or a link
  const [linkDialog, setLinkDialog] = useState(null)
  const [newPageOpen, setNewPageOpen] = useState(false)

  const pages = portal.pages || []
  const pageById = id => pages.find(page => page.id === id)
  const links = (portal.footerLinks || []).filter(
    link => !link.pageId || pageById(link.pageId),
  )
  const socials = (portal.socialLinks || []).filter(s => networkOf(s.network))
  // Slider values not saved yet, so the preview follows the slider.
  const [draft, setDraft] = useState({})
  useEffect(() => setDraft({}), [portal])
  const set = (patch, message) =>
    update(current => ({ ...current, ...patch }), message)
  const saveLinks = (next, message) => set({ footerLinks: next }, message)

  const upsert = (id, item, addedMessage) =>
    saveLinks(
      id
        ? links.map(link => (link.id === id ? { ...item, id } : link))
        : [...links, { ...item, id: newLinkId() }],
      id ? 'Link updated' : addedMessage,
    )

  const remove = link => {
    saveLinks(
      links.filter(l => l.id !== link.id),
      `"${link.label}" removed`,
    )
    setPageDialog(null)
    setLinkDialog(null)
  }

  const footerBg = portal.footerBackgroundColor || '#FFFFFF'
  const footerText = portal.footerTextColor || '#000000'
  const copyright = portal.copyright ?? defaultCopyright(shopName)

  const editLink = link =>
    link.pageId ? setPageDialog(link) : setLinkDialog(link)

  return (
    <div className={classes.page}>
      <Panel className={classes.panel}>
        <div className={classes.previewHead}>
          <Icon icon="tabler:eye" width={16} height={16} />
          <span>Preview · shown at the bottom of every portal page</span>
        </div>
        <div className={classes.previewStage}>
          <div className={classes.footerFrame}>
            <div className={classes.footerStripes} />
            <PortalFooterBar
              portal={portal}
              logo={workspaceLogo}
              shopName={shopName}
              logoSize={draft.footerLogoSize}
            />
          </div>
        </div>
      </Panel>

      <div className={classes.gridWide}>
        <div className={classes.column}>
          <Panel className={classes.panel}>
            <div className={classes.listHead}>
              <div style={{ minWidth: 0 }}>
                <div className={classes.cardTitle}>Links</div>
                <div className={classes.listSub}>
                  Drag to reorder. Link to your pages or to any address.
                </div>
              </div>
              <AddMenu
                options={[
                  {
                    icon: 'tabler:file-text',
                    color: '#015FFB',
                    title: 'Page',
                    description: 'Link to one of your portal pages.',
                    onClick: () => setPageDialog({}),
                  },
                  {
                    icon: 'tabler:link',
                    color: '#637381',
                    title: 'External link',
                    description: 'Privacy policy, terms, email or any website.',
                    onClick: () => setLinkDialog({}),
                  },
                ]}
              />
            </div>
            {links.length === 0 && (
              <div className={classes.empty}>No footer links yet.</div>
            )}
            <SortableList
              items={links}
              getKey={link => link.id}
              onMove={(from, to) => saveLinks(move(links, from, to))}
              renderRow={(link, index, dragProps) => {
                const page = link.pageId && pageById(link.pageId)
                return (
                  <ListRow
                    dragProps={dragProps}
                    icon={
                      page
                        ? 'tabler:file-text'
                        : (link.url || '').startsWith('mailto:')
                        ? 'tabler:mail'
                        : 'tabler:external-link'
                    }
                    label={link.label}
                    target={page ? page.path : link.url}
                    onClick={() => editLink(link)}
                    right={
                      <span className={classes.menuSlot}>
                        <RowMenu
                          editLabel="Edit link"
                          onEdit={() => editLink(link)}
                          onRemove={() => remove(link)}
                        />
                      </span>
                    }
                  />
                )
              }}
            />
          </Panel>

          <Panel className={classes.panel}>
            <div className={classes.listHead}>
              <div style={{ minWidth: 0 }}>
                <div className={classes.cardTitle}>Social profiles</div>
                <div className={classes.listSub}>
                  Shown as icons on the right side of the footer.
                </div>
              </div>
            </div>
            {socials.map(social => {
              const network = networkOf(social.network)
              return (
                <div key={social.network} className={classes.row}>
                  <span
                    className={classes.rowIcon}
                    style={{ color: '#002E47' }}
                  >
                    <Icon icon={network.icon} width={16} height={16} />
                  </span>
                  <span className={classes.socialLabel}>{network.label}</span>
                  <BlurInput
                    className={classes.socialInput}
                    value={social.url || ''}
                    placeholder={`https://${network.placeholder}`}
                    onSave={url =>
                      set({
                        socialLinks: socials.map(s =>
                          s.network === social.network
                            ? { ...s, url: url.trim() }
                            : s,
                        ),
                      })
                    }
                  />
                  <button
                    type="button"
                    title="Remove"
                    className={classes.socialRemove}
                    onClick={() =>
                      set(
                        {
                          socialLinks: socials.filter(
                            s => s.network !== social.network,
                          ),
                        },
                        `${network.label} removed`,
                      )
                    }
                  >
                    <Icon icon="tabler:trash" width={16} height={16} />
                  </button>
                </div>
              )
            })}
            {socials.length < SOCIAL_NETWORKS.length && (
              <div className={classes.socialAdd}>
                <span className={classes.socialAddLabel}>Add</span>
                {SOCIAL_NETWORKS.filter(
                  network => !socials.some(s => s.network === network.key),
                ).map(network => (
                  <button
                    key={network.key}
                    type="button"
                    className={classes.socialChip}
                    onClick={() =>
                      set(
                        {
                          socialLinks: [
                            ...socials,
                            { network: network.key, url: '' },
                          ],
                        },
                        null,
                      )
                    }
                  >
                    <Icon icon={network.icon} width={13} height={13} />
                    {network.label}
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <Panel className={classes.panel}>
          <div className={classes.listHead}>
            <div style={{ minWidth: 0 }}>
              <div className={classes.cardTitle}>Appearance</div>
              <div className={classes.listSub}>Logo, text and colors.</div>
            </div>
          </div>
          <div className={classes.appearance}>
            <ColorField
              label="Background color"
              value={footerBg}
              swatches={HEADER_BG_SWATCHES}
              onChange={footerBackgroundColor => set({ footerBackgroundColor })}
            />
            <ColorField
              label="Text color"
              value={footerText}
              swatches={TEXT_SWATCHES}
              onChange={footerTextColor => set({ footerTextColor })}
            />
            <ContrastWarning
              background={footerBg}
              text={footerText}
              what="Footer"
              onFix={footerTextColor => set({ footerTextColor })}
            />
            <BrandFields
              kind="footer"
              portal={portal}
              onChange={set}
              onDraft={setDraft}
            />
            <label className={classes.label} style={{ fontSize: 14 }}>
              Copyright
              <BlurInput
                className={classes.input}
                value={copyright}
                onSave={value => set({ copyright: value.trim() })}
              />
              <span className={classes.help}>Leave empty to hide.</span>
            </label>
            <div className={classes.divider} />
            <div className={classes.settingRow}>
              <div>
                <div className={classes.fieldTitle}>Show footer</div>
                <div className={classes.settingDesc}>
                  Turn off to hide the footer on portal pages.
                </div>
              </div>
              <Toggle
                checked={portal.showFooter !== false}
                onChange={showFooter => set({ showFooter })}
              />
            </div>
          </div>
        </Panel>
      </div>

      {pageDialog && (
        <FooterPageDialog
          link={pageDialog.id ? pageDialog : null}
          pages={pages}
          onClose={() => setPageDialog(null)}
          onSave={({ pageId, label }) => {
            upsert(
              pageDialog.id,
              { pageId, label },
              `"${label}" added to the footer`,
            )
            setPageDialog(null)
          }}
          onRemove={() => remove(pageDialog)}
          onNewPage={() => {
            setPageDialog(null)
            setNewPageOpen(true)
          }}
        />
      )}

      {linkDialog && (
        <LinkDialog
          link={linkDialog.id ? linkDialog : null}
          subtitle="Adds a link to your footer."
          onClose={() => setLinkDialog(null)}
          onSave={({ label, url, newTab }) => {
            upsert(
              linkDialog.id,
              { label, url, newTab },
              `"${label}" added to the footer`,
            )
            setLinkDialog(null)
          }}
          onRemove={() => remove(linkDialog)}
        />
      )}

      {newPageOpen && (
        <NewPageDialog
          host={host}
          showInMenu={false}
          onClose={() => setNewPageOpen(false)}
          onCreate={data =>
            createPage(data).then(saved => {
              if (!saved) return
              setNewPageOpen(false)
              const page = saved.pages.find(p => p.slug === data.slug)
              if (!page) return
              update(
                current => ({
                  ...current,
                  footerLinks: [
                    ...(current.footerLinks || []),
                    { id: newLinkId(), label: page.name, pageId: page.id },
                  ],
                }),
                null,
              ).then(() => openEditor(page))
            })
          }
        />
      )}
    </div>
  )
}

export default FooterTab
